import { useState, useEffect } from 'react';
import { FaPlay, FaPause, FaRedo, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './CountdownTimer.css';

const INITIAL_TIME = 25 * 60; // 25 minutes in seconds
const TIME_INCREMENT = 60; // Changed to 1 minute

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME);
  const [isRunning, setIsRunning] = useState(false);
  const [editingField, setEditingField] = useState(null); // 'mins', 'secs', or null
  const [inputValue, setInputValue] = useState({ mins: '25', secs: '00' });

  useEffect(() => {
    let timer;
    if (isRunning && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsRunning(false);
    }
    return () => clearInterval(timer);
  }, [isRunning, timeLeft]);

  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  const resetTimer = () => {
    setIsRunning(false);
    setTimeLeft(parseInt(inputValue.mins) * 60 + parseInt(inputValue.secs || 0));
  };

  const adjustTime = (amount) => {
    if (!isRunning) {
      const newTime = Math.max(1, timeLeft + amount);
      setTimeLeft(newTime);
      setInputValue({
        mins: Math.floor(newTime / 60).toString().padStart(2, '0'),
        secs: (newTime % 60).toString().padStart(2, '0')
      });
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleInputChange = (e, type) => {
    const value = e.target.value.replace(/\D/g, '');
    
    if (type === 'mins') {
      if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 99)) {
        const formattedValue = value.padStart(2, '0');
        setInputValue(prev => ({ ...prev, mins: formattedValue }));
        if (value !== '') {
          const newTime = parseInt(value) * 60 + parseInt(inputValue.secs || 0);
          setTimeLeft(newTime);
        }
      }
    } else if (type === 'secs') {
      if (value === '' || (parseInt(value) >= 0 && parseInt(value) <= 59)) {
        const formattedValue = value.padStart(2, '0');
        setInputValue(prev => ({ ...prev, secs: formattedValue }));
        if (value !== '') {
          const newTime = parseInt(inputValue.mins || 0) * 60 + parseInt(value);
          setTimeLeft(newTime);
        }
      }
    }
  };

  const handleInputBlur = () => {
    const mins = inputValue.mins === '' ? '25' : inputValue.mins.padStart(2, '0');
    const secs = inputValue.secs === '' ? '00' : inputValue.secs.padStart(2, '0');
    
    setInputValue({ mins, secs });
    setTimeLeft(parseInt(mins) * 60 + parseInt(secs));
    setEditingField(null);
  };

  const handleKeyDown = (e, type) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (type === 'mins') {
        const currentMins = parseInt(inputValue.mins || 0);
        if (currentMins < 99) {
          const newValue = (currentMins + 1).toString().padStart(2, '0');
          handleInputChange({ target: { value: newValue } }, 'mins');
        }
      } else {
        const currentSecs = parseInt(inputValue.secs || 0);
        if (currentSecs < 59) {
          const newValue = (currentSecs + 1).toString().padStart(2, '0');
          handleInputChange({ target: { value: newValue } }, 'secs');
        }
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (type === 'mins') {
        const currentMins = parseInt(inputValue.mins || 0);
        if (currentMins > 0) {
          const newValue = (currentMins - 1).toString().padStart(2, '0');
          handleInputChange({ target: { value: newValue } }, 'mins');
        }
      } else {
        const currentSecs = parseInt(inputValue.secs || 0);
        if (currentSecs > 0) {
          const newValue = (currentSecs - 1).toString().padStart(2, '0');
          handleInputChange({ target: { value: newValue } }, 'secs');
        }
      }
    }
  };

  return (
    <div className="countdown-timer">
      <div className="timer-adjust-group">
        <button 
          className="timer-adjust-button"
          onClick={() => adjustTime(-TIME_INCREMENT)}
          disabled={isRunning}
          aria-label="Decrease time"
        >
          <FaChevronLeft />
        </button>

        <div className="timer-display-container">
          {editingField ? (
            <div className="timer-input-group">
              <div 
                className="timer-input-wrapper"
                onClick={(e) => {
                  e.stopPropagation();
                  !isRunning && setEditingField('mins');
                }}
              >
                {editingField === 'mins' ? (
                  <input
                    type="text"
                    value={inputValue.mins}
                    onChange={(e) => handleInputChange(e, 'mins')}
                    onKeyDown={(e) => handleKeyDown(e, 'mins')}
                    onBlur={handleInputBlur}
                    className="timer-input"
                    maxLength="2"
                    autoFocus
                  />
                ) : (
                  <span className="timer-text">{inputValue.mins}</span>
                )}
              </div>
              <span className="timer-separator">:</span>
              <div 
                className="timer-input-wrapper"
                onClick={(e) => {
                  e.stopPropagation();
                  !isRunning && setEditingField('secs');
                }}
              >
                {editingField === 'secs' ? (
                  <input
                    type="text"
                    value={inputValue.secs}
                    onChange={(e) => handleInputChange(e, 'secs')}
                    onKeyDown={(e) => handleKeyDown(e, 'secs')}
                    onBlur={handleInputBlur}
                    className="timer-input"
                    maxLength="2"
                    autoFocus
                  />
                ) : (
                  <span className="timer-text">{inputValue.secs}</span>
                )}
              </div>
            </div>
          ) : (
            <div 
              className="timer-display"
              onClick={() => !isRunning && setEditingField('mins')}
            >
              {formatTime(timeLeft)}
            </div>
          )}
        </div>

        <button 
          className="timer-adjust-button"
          onClick={() => adjustTime(TIME_INCREMENT)}
          disabled={isRunning}
          aria-label="Increase time"
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="timer-controls">
        <button 
          className="timer-button"
          onClick={toggleTimer}
          aria-label={isRunning ? 'Pause timer' : 'Start timer'}
        >
          {isRunning ? <FaPause /> : <FaPlay />}
        </button>
        <button 
          className="timer-button"
          onClick={resetTimer}
          aria-label="Reset timer"
        >
          <FaRedo />
        </button>
      </div>
    </div>
  );
};

export default CountdownTimer; 